
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "./Button";
import { IconButton } from "./Button";
import { cn } from "./cn";
import { TextField } from "./Field";
import styles from "./overlay.module.css";
import type { Tone } from "./status";
import { didTooltipConsumeEscape } from "./Tooltip";
import { useOpenerFocus } from "./use-opener-focus";
import { VisuallyHidden } from "./VisuallyHidden";

export type DialogSize = "sm" | "md" | "lg";

const SIZE_CLASS: Record<DialogSize, string> = {
  sm: styles.dialogSm,
  md: styles.dialogMd,
  lg: styles.dialogLg,
};

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  size?: DialogSize;
  footer?: ReactNode;
  className?: string;
  initialFocus?: RefObject<HTMLElement | null>;
  returnFocusTo?: RefObject<HTMLElement | null>;
  children: ReactNode;
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  footer,
  className,
  initialFocus,
  returnFocusTo,
  children,
}: DialogProps) {
  const { t } = useTranslation();
  const { rememberOpener, returnFocusToOpener } = useOpenerFocus();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.blanket} />
        <DialogPrimitive.Content
          className={cn(styles.dialog, SIZE_CLASS[size], className)}
          onOpenAutoFocus={(event) => {
            rememberOpener();
            const target = initialFocus?.current;
            if (!target) return;
            event.preventDefault();
            target.focus();
          }}
          onCloseAutoFocus={(event) => {
            const target = returnFocusTo?.current;
            if (!target || !target.isConnected) {
              returnFocusToOpener(event);
              return;
            }
            event.preventDefault();
            target.focus();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape" && didTooltipConsumeEscape(event.nativeEvent)) onOpenChange(false);
          }}
        >
          <div className={styles.overlayHeader}>
            <div className={styles.overlayHeaderCopy}>
              <DialogPrimitive.Title title={title} className={styles.overlayTitle}>
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className={styles.overlaySubtitle}>
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <VisuallyHidden>
                  <DialogPrimitive.Description>{title}</DialogPrimitive.Description>
                </VisuallyHidden>
              )}
            </div>
            <div className={styles.overlayHeaderActions}>
              <DialogPrimitive.Close asChild>
                <IconButton
                  label={t("common.close")}
                  showTooltip={false}
                  icon={<X size={16} strokeWidth={1.75} aria-hidden="true" />}
                />
              </DialogPrimitive.Close>
            </div>
          </div>
          <div className={styles.overlayBody}>{children}</div>
          {footer ? <div className={styles.overlayFooter}>{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Extract<Tone, "danger" | "warning">;
  typedConfirmation?: string;
  typedConfirmationLabel?: string;
  returnFocusTo?: RefObject<HTMLElement | null>;
  onConfirm: () => void | Promise<void>;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  typedConfirmation,
  typedConfirmationLabel,
  returnFocusTo,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const typedRef = useRef<HTMLInputElement | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setBusy(false);
    }
  }, [open]);

  const blocked = Boolean(typedConfirmation) && typed.trim() !== typedConfirmation;

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className={styles.blanket} />
        <AlertDialogPrimitive.Content
          className={cn(styles.dialog, styles.dialogSm)}
          onOpenAutoFocus={(event) => {
            if (!typedConfirmation) return;
            event.preventDefault();
            typedRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            const target = returnFocusTo?.current;
            if (!target || !target.isConnected) return;
            event.preventDefault();
            target.focus();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape" && didTooltipConsumeEscape(event.nativeEvent)) onOpenChange(false);
          }}
        >
          <div className={styles.overlayHeader}>
            <div className={styles.overlayHeaderCopy}>
              <AlertDialogPrimitive.Title title={title} className={styles.overlayTitle}>
                {title}
              </AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className={styles.overlaySubtitle}>
                {message}
              </AlertDialogPrimitive.Description>
            </div>
          </div>
          {typedConfirmation ? (
            <div className={styles.overlayBody}>
              <TextField
                ref={typedRef}
                label={typedConfirmationLabel ?? t("ui.typeToConfirm", { value: typedConfirmation })}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
              />
            </div>
          ) : null}
          <div className={styles.overlayFooter}>
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="subtle" disabled={busy}>
                {cancelLabel ?? t("common.cancel")}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button
              variant={tone === "danger" ? "danger" : "warning"}
              loading={busy}
              disabled={blocked}
              onClick={() => void confirm()}
            >
              {confirmLabel ?? t("common.confirm")}
            </Button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
